import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { validate } from '@/utils/validations';
import { Input, Select, UnofficalTruckerPhoneAlert } from '@/components';
import { useAppContext } from '@/context';
import {
  useSetFormValue,
  useErrorFieldHandler,
  cityNoContent,
  dateYmdToDmy,
  dateSubstractYear,
  dateAddYear,
  getMask,
  deepMerge,
  birthdateRange,
} from '@/utils';
import { FormContainer, FormItem, FormRow, LoadingInput, LoadingInputWrapper } from '@/components/FormWrapper/Steps';
import {
  useTruckerAutoFillVerify,
  useCitiesFilter,
  useEditFocusHandler,
  useZipcode,
} from '@/components/FormWrapper/hooks';
import { makeCnhCategoriesList, makeStateList } from '@/data';
import schema from './validation';
import { useRedirectToRegister, useToast, useTracker } from '@/hooks';
import { DynamicObject, Errors } from './types';
import { getEmptyTrucker } from '@/context/reducers';
import { UnofficalPhoneCallout } from './UnofficalPhoneCallout';
import { FormTrucker } from '@/types';
import { useIsDisabledField } from './functions';
import { NOT_FOUND_CUSTOM } from '@/constants';
import { CellphoneInput } from '@/components/input-cellphone-anonymize/input-cellphone-anonymized';

export const Trucker: React.FC = () => {
  const { dispatch, state } = useAppContext();
  const tracker = useTracker();
  const [errorsState, setErrors] = useState<Errors>();
  const form = useMemo(() => ({ ...state.risk.data }), [state.risk.data]);
  const truckerProfile = useMemo(() => ({ ...state.truckerProfile }), [state.truckerProfile]);
  const triedToSubmit = useMemo(() => state.risk.triedToSubmit, [state.risk.triedToSubmit]);
  const setFormValue = useSetFormValue(dispatch);
  const citiesFilter = useCitiesFilter();
  const errorFieldHandler = useErrorFieldHandler(dispatch);
  const states = useMemo(makeStateList, []);
  const cnhCategories = useMemo(makeCnhCategoriesList, []);
  const redirectToRegister = useRedirectToRegister();
  const autoFillVerify = useTruckerAutoFillVerify(state);
  const toast = useToast();
  const { handleFocus, handleBlur } = useEditFocusHandler();
  const { onUpdateZipcode, isLoadingZipcode } = useZipcode({ type: 'single' });
  const { isDisabledField, isResubmit, isLoadedProfile } = useIsDisabledField();
  const [searchParams] = useSearchParams();
  const search = searchParams.get('search');

  const showUnofficialPhoneAlert = useMemo(
    () => state.risk.showUnofficialPhoneAlert,
    [state.risk.showUnofficialPhoneAlert],
  );

  const showUnofficialPhoneCallout = useMemo(
    () =>
      !state.risk.showUnofficialPhoneAlert &&
      !truckerProfile.data.isDriverPhone &&
      truckerProfile.isLoaded &&
      truckerProfile.data.isRegistered,
    [state.risk, truckerProfile],
  );

  const setTruckerDataToForm = (cpf: string, cellPhone: string, profile: Partial<FormTrucker>) => {
    const data: Partial<FormTrucker> = { ...profile, cpf, cellPhone };
    setFormValue('trucker', deepMerge(getEmptyTrucker(), data));
  };

  const firstCnhRange = useMemo(() => {
    const now = new Date(Date.now());
    const min = dateYmdToDmy(dateSubstractYear(now, 82).toJSON());
    const max = dateYmdToDmy(now.toJSON());
    return { min, max };
  }, []);

  const cnhExpirationDateRange = useMemo(() => {
    const now = new Date(Date.now());
    const min = dateYmdToDmy(new Date(Date.UTC(2022, 5, 1)).toJSON());
    const max = dateYmdToDmy(dateAddYear(now, 10).toJSON());
    return { min, max };
  }, []);

  const handleUnofficialTruckerProne = (cpf: string, cellPhone: string, personId: string) => {
    dispatch({ action: 'set_show_unofficial_phone_alert', payload: true });

    tracker.dispatchWithUserData('risk_management_form_phonealert_viewed', {
      trucker: { id: personId, cellPhone },
    });
    setTruckerDataToForm(cpf, cellPhone, getEmptyTrucker());
  };

  const onConfirmUnofficialTrukerPhoneAlert = () => {
    dispatch({ action: 'set_show_unofficial_phone_alert', payload: false });
    setTruckerDataToForm(form.trucker.cpf, form.trucker.cellPhone, truckerProfile.data as FormTrucker);
    dispatch({ action: 'trucker_profile_set_all_done', payload: true });
    tracker.dispatchWithUserData('risk_management_form_phonealert_checked', {
      trucker: { id: truckerProfile.data?.personId, cellPhone: form.trucker.cellPhone },
    });

    if (!truckerProfile.data.profileId && !truckerProfile.data.riskId) {
      toast.add({
        title: NOT_FOUND_CUSTOM,
        type: 'neutral',
      });
    }
  };

  const onRefuseUnofficialTrukerPhoneAlert = () => {
    tracker.dispatchWithUserData('risk_management_form_phonealert_canceled', {
      trucker: { id: truckerProfile.data?.personId, cellPhone: form.trucker.cellPhone },
    });

    redirectToRegister();
  };

  const handleProfileResult = (profile: FormTrucker) => {
    if (Boolean(search)) return;
    dispatch({ action: 'trucker_profile_update_data', payload: profile });
    setErrors({});

    if (profile.isRegistered && !profile.truckerIsActive) {
      toast.add({
        title: 'Cadastro não ativo na plataforma',
        description: 'Peça ao motorista que entre em contato com a central de atendimento.',
        timeout: 15000,
        type: 'neutral',
      });
      return;
    }

    if (!profile.profileId && !profile.riskId) {
      dispatch({ action: 'trucker_profile_clean_data', payload: profile });

      if (profile.isRegistered && !profile.isDriverPhone) {
        handleUnofficialTruckerProne(profile.cpf, profile.cellPhone, profile.personId);
        return;
      }

      const emptyTrucker = getEmptyTrucker();
      emptyTrucker.isDriverPhone = profile.isDriverPhone;
      setTruckerDataToForm(profile.cpf, profile.cellPhone, emptyTrucker);
      dispatch({ action: 'trucker_profile_set_all_done', payload: true });

      toast.add({
        title: NOT_FOUND_CUSTOM,
        type: 'neutral',
      });

      return;
    }

    tracker.dispatchWithUserData('risk_management_form_trucker_searched', {
      trucker: { id: profile.personId },
    });

    if (profile.isDriverPhone) {
      setTruckerDataToForm(form.trucker.cpf, form.trucker.cellPhone, profile);
      toast.add({ title: 'Dados encontrados com sucesso!', type: 'success' });
      dispatch({ action: 'trucker_profile_set_all_done', payload: true });
      return;
    }

    handleUnofficialTruckerProne(form.trucker.cpf, form.trucker.cellPhone, profile.personId);
  };

  useEffect(() => {
    tracker.dispatchWithUserData('risk_management_form_trucker_viewed');
    handleProfileResult(truckerProfile.data as FormTrucker);
  }, []);

  useEffect(() => {
    if (triedToSubmit) {
      const errors = validate(schema, form);
      setErrors(errors);
      errorFieldHandler(errors as DynamicObject);
    }
  }, [triedToSubmit, form]);

  useEffect(() => {
    if (triedToSubmit) {
      dispatch({ action: 'risk_set_is_valid', payload: !Boolean(errorsState) });
    }
  }, [errorsState]);

  return (
    <FormContainer>
      <FormRow>
        <FormItem>
          <Input
            error={errorsState?.trucker?.cpf}
            label="CPF"
            mask={getMask('cpf')}
            unmaskedValue
            name="trucker.cpf"
            onUpdate={(value) => setFormValue('trucker.cpf', value)}
            placeholder="000.000.000-00"
            value={form.trucker.cpf}
            disabled
          />
        </FormItem>
        <FormItem>
          <CellphoneInput
            label="Telefone"
            mask={getMask('cellPhoneAnonymyzed')}
            unmaskedValue
            onUpdate={(value) => setFormValue('trucker.cellPhone', value)}
            placeholder="(00) 00000-0000"
            value={form.trucker.cellPhone}
            error={errorsState?.trucker?.cellPhone}
            name="trucker.cellPhone"
            disabled
            anonymizeOnBlur
          />
        </FormItem>
      </FormRow>
      {showUnofficialPhoneCallout && (
        <FormRow>
          <FormItem md={4} lg={2}>
            <UnofficalPhoneCallout />
          </FormItem>
        </FormRow>
      )}
      <FormRow>
        <FormItem md={2} lg={2}>
          <Input
            label="Nome completo"
            onUpdate={(value) => setFormValue('trucker.name', (value as string).replace(/\d/g, ''))}
            placeholder="Digite o nome do motorista"
            value={form.trucker.name}
            error={errorsState?.trucker?.name}
            name="trucker.name"
            disabled={isDisabledField({ field: 'name' })}
          />
        </FormItem>
      </FormRow>
      <FormRow>
        <FormItem>
          <Input
            label="Data de nascimento"
            onUpdate={(value) => setFormValue('trucker.birthday', value)}
            type={!isResubmit && (!isLoadedProfile || autoFillVerify('birthday')) ? 'text' : 'date'}
            value={form.trucker.birthday}
            error={errorsState?.trucker?.birthday}
            name="trucker.birthday"
            minDate={birthdateRange().min}
            maxDate={birthdateRange().max}
            placeholder="DD/MM/AAAA"
            disabled={isDisabledField({ field: 'birthday' })}
          />
        </FormItem>
        <FormItem>
          <Input
            label="Nome do pai (opcional)"
            onUpdate={(value) => setFormValue('trucker.fatherName', (value as string).replace(/\d/g, ''))}
            placeholder="Nome completo do pai"
            value={form.trucker.fatherName}
            error={errorsState?.trucker?.fatherName}
            name="trucker.fatherName"
            disabled={isDisabledField({ field: 'fatherName' })}
          />
        </FormItem>
        <FormItem>
          <Input
            label="Nome da mãe"
            onUpdate={(value) => setFormValue('trucker.motherName', (value as string).replace(/\d/g, ''))}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="Nome completo da mãe"
            value={form.trucker.motherName}
            error={errorsState?.trucker?.motherName}
            name="trucker.motherName"
            disabled={isDisabledField({ field: 'motherName' })}
          />
        </FormItem>
        <FormItem>
          <Select
            label="Categoria da CNH"
            options={cnhCategories}
            scrollable
            onUpdate={(value) => setFormValue('trucker.cnh.category', value)}
            value={form.trucker.cnh.category}
            error={errorsState?.trucker?.cnh?.category}
            name="trucker.cnh.category"
            disabled={isDisabledField({ field: 'cnh.category' })}
          />
        </FormItem>
      </FormRow>
      <FormRow>
        <FormItem>
          <Input
            label="CNH registro"
            mask={getMask(
              'cnh',
              form.trucker.cnh.registrationNumber.includes('*') || autoFillVerify('cnh.registrationNumber'),
            )}
            onUpdate={(value) => setFormValue('trucker.cnh.registrationNumber', value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="00000000000"
            value={form.trucker.cnh.registrationNumber}
            error={errorsState?.trucker?.cnh?.registrationNumber}
            name="trucker.cnh.registrationNumber"
            disabled={isDisabledField({ field: 'cnh.registrationNumber' })}
          />
        </FormItem>
        <FormItem>
          <Input
            label="Validade CNH"
            onUpdate={(value) => setFormValue('trucker.cnh.expirationDate', value)}
            type={!isResubmit && (!isLoadedProfile || autoFillVerify('cnh.expirationDate')) ? 'text' : 'date'}
            value={form.trucker.cnh.expirationDate}
            error={errorsState?.trucker?.cnh?.expirationDate}
            name="trucker.cnh.expirationDate"
            minDate={cnhExpirationDateRange.min}
            maxDate={cnhExpirationDateRange.max}
            placeholder="DD/MM/AAAA"
            disabled={isDisabledField({ field: 'cnh.expirationDate' })}
          />
        </FormItem>
        <FormItem>
          <Input
            label="Primeira CNH"
            onUpdate={(value) => setFormValue('trucker.cnh.dateFirstLicense', value)}
            type={!isResubmit && (!isLoadedProfile || autoFillVerify('cnh.dateFirstLicense')) ? 'text' : 'date'}
            value={form.trucker.cnh.dateFirstLicense}
            error={errorsState?.trucker?.cnh?.dateFirstLicense}
            name="trucker.cnh.dateFirstLicense"
            minDate={firstCnhRange.min}
            maxDate={firstCnhRange.max}
            placeholder="DD/MM/AAAA"
            disabled={isDisabledField({ field: 'cnh.dateFirstLicense' })}
          />
        </FormItem>
      </FormRow>
      <FormRow>
        <FormItem>
          <Select
            label="UF CNH"
            onUpdate={(value) => setFormValue('trucker.cnh.stateEmission', value)}
            optionLabel="displayName"
            optionValue="initial"
            emitValue
            options={states}
            placeholder="UF"
            scrollable
            value={form.trucker.cnh.stateEmission}
            error={errorsState?.trucker?.cnh?.stateEmission}
            name="trucker.cnh.stateEmission"
            disabled={isDisabledField({ field: 'cnh.stateEmission' })}
          />
        </FormItem>
        <FormItem>
          <Input
            label="Segurança CNH"
            mask={getMask(
              'cnhSecurity',
              form.trucker.cnh.securityNumber.includes('*') || autoFillVerify('cnh.securityNumber'),
            )}
            onUpdate={(value) => setFormValue('trucker.cnh.securityNumber', value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            value={form.trucker.cnh.securityNumber}
            placeholder="00000000000"
            error={errorsState?.trucker?.cnh?.securityNumber}
            name="trucker.cnh.securityNumber"
            disabled={isDisabledField({ field: 'cnh.securityNumber' })}
          />
        </FormItem>
      </FormRow>
      <FormRow>
        <LoadingInputWrapper>
          <Input
            label="CEP"
            mask={getMask('zipCode')}
            unmaskedValue
            onUpdate={onUpdateZipcode}
            placeholder="00000-000"
            value={form.trucker.address.zipCode}
            error={errorsState?.trucker?.address?.zipCode}
            name="trucker.address.zipCode"
            disabled={isDisabledField({
              field: 'address.zipCode',
              isDisabled: () =>
                !isResubmit && (!isLoadedProfile || isLoadingZipcode() || autoFillVerify('address.zipCode')),
            })}
          />
          {isLoadingZipcode() && <LoadingInput />}
        </LoadingInputWrapper>
      </FormRow>
      <FormRow>
        <FormItem>
          <Input
            label="Endereço"
            onUpdate={(value) => setFormValue('trucker.address.street', value)}
            placeholder="Insira o endereço"
            value={form.trucker.address.street}
            error={errorsState?.trucker?.address?.street}
            name="trucker.address.street"
            disabled={isDisabledField({ field: 'address.street' })}
          />
        </FormItem>
        <FormItem>
          <Input
            mask={getMask('addressNumber')}
            label="Número (opcional)"
            onUpdate={(value) => setFormValue('trucker.address.number', value)}
            placeholder="0000"
            value={form.trucker.address.number || ''}
            error={errorsState?.trucker?.address?.number}
            name="trucker.address.number"
            disabled={isDisabledField({ field: 'address.number' })}
          />
        </FormItem>
        <FormItem>
          <Input
            label="Complemento (opcional)"
            onUpdate={(value) => setFormValue('trucker.address.complement', value)}
            placeholder="Caso exista complemento"
            value={form.trucker.address.complement}
            error={errorsState?.trucker?.address?.complement}
            name="trucker.address.complement"
            disabled={isDisabledField({ field: 'address.complement' })}
          />
        </FormItem>
        <FormItem>
          <Input
            label="Bairro (opcional)"
            onUpdate={(value) => setFormValue('trucker.address.neighborhood', value)}
            placeholder="Insira o bairro"
            value={form.trucker.address.neighborhood}
            error={errorsState?.trucker?.address?.neighborhood}
            name="trucker.address.neighborhood"
            disabled={isDisabledField({ field: 'address.neighborhood' })}
          />
        </FormItem>
      </FormRow>
      <FormRow>
        <FormItem sm={4} md={2} lg={2}>
          <Select
            label="Cidade"
            onUpdate={(value) => setFormValue('trucker.address.location', value)}
            optionLabel="place"
            optionValue="place"
            options={[]}
            filter={citiesFilter}
            placeholder="Digite para buscar a cidade"
            scrollable
            noContent={cityNoContent}
            noArrow
            filterOnFocus
            inputAutoFocus
            lazy
            debounceFilter={350}
            value={form.trucker.address.location}
            error={errorsState?.trucker?.address?.location}
            name="trucker.address.location"
            disabled={isDisabledField({ field: 'address.location' })}
          />
        </FormItem>
      </FormRow>
      <UnofficalTruckerPhoneAlert
        isOpen={showUnofficialPhoneAlert}
        onConfirm={onConfirmUnofficialTrukerPhoneAlert}
        onRefuse={onRefuseUnofficialTrukerPhoneAlert}
      />
    </FormContainer>
  );
};
